import React, { useState, useMemo } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, Paper,
  Alert, Snackbar,
} from '@mui/material';
import { Save as SaveIcon, RestartAlt as ResetIcon } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/rpc';

/**
 * System Labels admin panel — lets root admins customise application-wide UI labels.
 * Maps to GWT's system-label management dialog.
 */
export const SystemLabelsPanel = () => {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['systemLabels'],
    queryFn: () => api.getSystemLabelsSettings(),
    staleTime: 60_000,
  });

  // Local editable copy
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // When the query resolves, initialise the edit state once (if not already dirty)
  const serverLabels = useMemo(
    () => (data ?? {}) as Record<string, string>,
    [data],
  );

  React.useEffect(() => {
    if (!dirty) setEdits(serverLabels);
  }, [serverLabels, dirty]);

  const setValue = (key: string, val: string) => {
    setEdits(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.saveSystemLabelsSettings(JSON.stringify(edits));
      await qc.invalidateQueries({ queryKey: ['systemLabels'] });
      setDirty(false);
      setSnack({ msg: 'System labels saved.', severity: 'success' });
    } catch {
      setSnack({ msg: 'Save failed — check console for details.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setEdits(serverLabels);
    setDirty(false);
  };

  const keys = useMemo(() => Object.keys(edits).sort(), [edits]);

  if (isLoading) return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 3 }}>
      <CircularProgress size={20} />
      <Typography sx={{ fontSize: 13 }}>Loading system labels…</Typography>
    </Box>
  );

  if (error) return (
    <Alert severity="error" sx={{ m: 2 }}>Failed to load system labels: {String(error)}</Alert>
  );

  return (
    <Box sx={{ p: 2, maxWidth: 720 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6" sx={{ fontSize: 14, fontWeight: 600, flex: 1 }}>
          System Label Overrides
        </Typography>
        <Button
          size="small"
          startIcon={<ResetIcon />}
          onClick={handleReset}
          disabled={!dirty || saving}
          sx={{ fontSize: 11 }}
        >
          Reset
        </Button>
        <Button
          size="small"
          startIcon={<SaveIcon />}
          variant="contained"
          onClick={handleSave}
          disabled={!dirty || saving}
          sx={{ fontSize: 11 }}
        >
          Save All
        </Button>
      </Box>

      <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>
        Override application-wide UI labels (e.g. rename "Patient" to "Client"). Changes take effect
        after the next page reload. Leave a field blank to use the default value.
      </Typography>

      {keys.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>
          No label overrides configured. Contact support if system labels are not loading.
        </Typography>
      ) : (
        <Paper variant="outlined">
          <Table size="small" sx={{ '& td,& th': { fontSize: 12, py: 0.5 } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600, width: '40%' }}>Label Key</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((key) => (
                <TableRow key={key} hover>
                  <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: 11 }}>
                    {key}
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={edits[key] ?? ''}
                      onChange={(e) => setValue(key, e.target.value)}
                      size="small"
                      fullWidth
                      variant="standard"
                      inputProps={{ style: { fontSize: 12 } }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={3500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack?.severity ?? 'success'} sx={{ fontSize: 12 }}>
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};
